import { Space3 } from '@moia-dev/pace-core-web/pace-spacing'
import { Accordion, Button, Checkbox } from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Represents a single filter option that can be selected
 */
export interface FilterOption<T = string> {
  /** Unique value of the option */
  value: T
  /** Display label of the option */
  label: string
}

/**
 * Filter configuration for drawer filters
 */
export interface DrawerFilter<T = string> {
  /** Unique identifier for the filter */
  id: string
  /** Display label for the filter */
  label: string
  /** Filter options that can be selected */
  options: FilterOption<T>[]
  /** Whether the filter accordion should be initially expanded */
  initiallyExpanded?: boolean
}

/**
 * Generic filter state interface
 * Maps filter IDs to their selected values
 */
export type FilterState = Record<string, string[]>

export interface FilterAccordionProps {
  /** Filter configuration */
  filter: DrawerFilter
  /** Current filter state */
  selectedFilters: FilterState
  /** Function to update the filter state */
  setSelectedFilters: (filters: FilterState) => void
}

/**
 * A generic accordion component for displaying filter options with checkboxes
 */
export const FilterAccordion = ({
  filter,
  selectedFilters,
  setSelectedFilters,
}: FilterAccordionProps) => {
  const { t } = useTranslation()
  const [maxVisibleOptions, setMaxVisibleOptions] = useState<number>(5)
  const optionsLength = filter.options.length

  // Clear all selected options for this filter
  const clearAll = () => {
    const updatedFilters = { ...selectedFilters }
    delete updatedFilters[filter.id]
    setSelectedFilters(updatedFilters)
  }

  // Toggle selection for a specific option
  const handleSelectOption = (optionValue: string) => {
    const updatedFilters = { ...selectedFilters }

    if (!updatedFilters[filter.id]) {
      // If filter doesn't exist yet, create it with the selected option
      updatedFilters[filter.id] = [optionValue]
    } else {
      // If filter exists, toggle the option
      const filterOptions = [...updatedFilters[filter.id]]
      const optionIndex = filterOptions.indexOf(optionValue)

      if (optionIndex === -1) {
        // Option not selected yet, add it
        filterOptions.push(optionValue)
      } else {
        // Option already selected, remove it
        filterOptions.splice(optionIndex, 1)
      }

      if (filterOptions.length === 0) {
        // No options left, remove the filter
        delete updatedFilters[filter.id]
      } else {
        // Update selected options
        updatedFilters[filter.id] = filterOptions
      }
    }

    setSelectedFilters(updatedFilters)
  }

  // Get selected options for this filter
  const selectedOptions = selectedFilters[filter.id] || []
  const hasSelectedOptions = selectedOptions.length > 0

  return (
    <div style={{ position: 'relative' }}>
      {hasSelectedOptions && (
        <Button
          style={{
            position: 'absolute',
            right: '50px',
            top: '6px',
            zIndex: 2,
          }}
          variant="plain"
          label={t('patterns_clear_label')}
          onClick={clearAll}
          size="compact"
        />
      )}
      <Accordion
        open={filter.initiallyExpanded ?? false}
        title={filter.label}
        compact
      >
        {filter.options.slice(0, maxVisibleOptions).map(option => (
          <div
            key={option.value}
            css={{
              paddingBottom: Space3,
            }}
          >
            <Checkbox
              checked={selectedOptions.includes(option.value)}
              onChange={() => handleSelectOption(option.value)}
              label={option.label}
            />
          </div>
        ))}
        {optionsLength > maxVisibleOptions && (
          <Button
            css={{ display: 'flex', marginTop: Space3 }}
            variant="plain"
            label={t('patterns_showAll_label')}
            onClick={() => setMaxVisibleOptions(optionsLength)}
          />
        )}
      </Accordion>
    </div>
  )
}
